import { useState } from "react"
import { default as NextLink } from "next/link"
import NextImage from "next/image"
import { usePathData } from "@/lib/contexts/appContext"
import Block from "@/components/wrappers/block"
import LogoSpinner from "@/components/common/loading/logo-spinner"
import styles from "./image.module.css"
import classNames from "classnames"
import { getLinkRel, getLinkTarget } from "@/lib/helpers/linkAttrs"

export default function Image({ block, settings }) {
  const [isLoading, setLoading] = useState(true)
  const { template } = usePathData()
  const { attrs } = block
  const { image } = attrs

  if (!image || !image.url) {
    return null
  }

  const isSmall = ["small", "medium"].includes(attrs.size) || image.width < 640

  const Link = ({ children }) => {
    if (!attrs.link) {
      return <>{children}</>
    }

    return (
      <NextLink
        href={attrs.link.url}
        prefetch={false}
        rel={getLinkRel(attrs.link) ?? attrs.link.rel}
        target={getLinkTarget(attrs.link.target) ?? undefined}>
        {children}
      </NextLink>
    )
  }

  const Element = ({ attrs, image }) => {
    const imageAttrs = {
      src: image.url,
      alt: image.alt,
      width: image.width,
      height: image.height,
      sizes: image.sizes,
      loading: "lazy",
      onLoad: () => setLoading(false)
    }

    const creditsText = attrs.editedCredits
      ? attrs.credits
      : image.credit
      ? `${image.type}: ${image.credit}`
      : null

    return (
      <figure
        className={classNames([
          "size-" + attrs.size,
          "ratio-" + attrs.ratio.replace(":", "-"),
          "align-" + attrs.align,
          {
            "has-ratio": attrs.ratio !== "original",
            "is-loading": isLoading,
            "inner-left-align": attrs.align === "left" && isSmall,
            "in-page-group": settings?.grouped
          }
        ])}>
        <Link>
          {image.url && (
            <div className="image-wrapper">
              {isLoading && <LogoSpinner />}
              <NextImage {...imageAttrs} />
            </div>
          )}
        </Link>
        {(attrs.caption || creditsText) && (
          <div className="caption-wrapper">
            {attrs.caption && (
              <figcaption
                dangerouslySetInnerHTML={{
                  __html: attrs.caption
                }}></figcaption>
            )}
            {creditsText && (
              <div
                dangerouslySetInnerHTML={{
                  __html: creditsText
                }}></div>
            )}
          </div>
        )}
      </figure>
    )
  }

  const widths = {
    full: "bleed",
    large: "large",
    medium: "column",
    small: "column"
  }

  const gapFallback = template === "article" ? "content" : "normal"
  const gap = settings?.gap || attrs?.blockGap || gapFallback
  const width =
    attrs.align === "center" && attrs.ratio !== "original"
      ? widths[attrs.size] || "large"
      : "bleed"

  return (
    <Block
      block={block}
      className={classNames(styles.wrapper, {
        "is-article": template === "article"
      })}
      innerWrapper={true}
      width={settings?.width ?? width}
      gap={gap}>
      <Element {...{ attrs, image }} />
    </Block>
  )
}
